// Pure decision logic for the opt-in autosave timer (EditorApp.jsx). Kept
// free of React/store imports so it's unit-testable in isolation — callers
// extract primitives from redux state and pass them in.
//
// One timer tick, two questions: shouldShowUnsavedPulse says whether to show
// the dot next to Save (true whenever autosave is on and something is
// unsaved, regardless of why a write hasn't landed — e.g. suspended by an
// unresolved conflict, so the user still has a signal); shouldAutosaveTick
// says whether THIS tick should actually fire a save (same "on and changed"
// base, plus not while another write is in flight and not while suspended).
// The baseline both diff against (_autosaveLastSignature) is updated by
// every successful save (manual Save, ExitModal, the nav guard's "Save and
// leave", post-import, AnimatorPanel's onSaved, and the autosave tick's own
// success) — never by a tick that only pulses.
//
// Change detection: this editor has no dirty/isDirty flag (see the save-path
// note in EditorNavBar.jsx). redux-undo's history depth (`past.length`) is
// used as the primary proxy, but a handful of action types are deliberately
// filtered out of undo history while still mutating real content —
// ADD_SOUND, UPLOAD_TEMPLATE/DELETE_TEMPLATE, and the bulk-animate-apply
// path (window.bulkAnimateApplying) — see reducers/reducers.jsx's
// undoable() filter. `past.length` alone misses those.
//
// The fix isn't "compare all of `present`": most filtered-out action types
// exist *because* they're pure UI/selection state (SELECT_BOX, INDEX_SELECT,
// EXPAND_NAV_ITEM, SET_BUSY, ...), and those touch `present` just as much as
// real edits do (a new reference every dispatch). Comparing the whole
// `present` object would make autosave fire on nearly every tick during
// normal use (e.g. merely clicking to select a different box moves
// `boxSelected`, which changes `present`'s reference) — not "only when
// there are changes". Instead this compares `present` MINUS the known
// pure-UI/selection keys, so navigation doesn't look like an edit.
//
// Known residual gap (documented, not fixed here — see task report): a
// couple of filtered actions share a reducer case with real content updates
// (e.g. TOGGLE_TEXT_EDITOR sets pluginToolbarsById[id].showTextEditor via
// the same case as UPDATE_PLUGIN_TOOLBAR, and EXPAND_NAV_ITEM sets
// navItemsById[id].isExpanded) — entering text-edit mode or expanding a
// scene-tree node can occasionally look like a change even with no edit.
// Untangling that needs per-field diffing, which is exactly the general
// dirty-tracking system this task was told not to build.
const NON_CONTENT_KEYS = new Set([
  "version",
  "boxSelected",
  "boxSelectedAll",
  "boxLevelSelected",
  "indexSelected",
  "navItemSelected",
  "containedViewSelected",
  "displayMode",
  "isBusy",
  "lastActionDispatched",
]);

export function getUndoSignature(undoGroupState) {
  const present = undoGroupState.present || {};
  const content = {};
  for (const key of Object.keys(present)) {
    if (!NON_CONTENT_KEYS.has(key)) content[key] = present[key];
  }
  return {
    pastLength: (undoGroupState.past || []).length,
    content,
  };
}

export function hasUndoSignatureChanged(prevSignature, nextSignature) {
  if (!prevSignature) return true;
  if (nextSignature.pastLength !== prevSignature.pastLength) return true;
  return Object.keys(nextSignature.content).some(
    (key) => nextSignature.content[key] !== prevSignature.content[key]
  );
}

// Should a timer tick show the unsaved-changes pulse?
export function shouldShowUnsavedPulse({ enabled, prevSignature, nextSignature }) {
  if (!enabled) return false;
  return hasUndoSignatureChanged(prevSignature, nextSignature);
}

// Should a timer tick actually fire a save?
export function shouldAutosaveTick({ enabled, suspended, isSaving, prevSignature, nextSignature }) {
  if (!enabled || suspended || isSaving) return false;
  return hasUndoSignatureChanged(prevSignature, nextSignature);
}
